import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, CheckIcon } from '@radix-ui/react-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { PromptAgentType } from './types';
import { getAgentTypes } from './promptLibraryData';
import { useTheme } from '../../components/Theme/ThemeProvider';

interface AgentTypeSelectProps {
  /** Single-select mode: currently selected value */
  value?: PromptAgentType | null;
  /** Single-select callback */
  onChange?: (type: PromptAgentType) => void;
  /** Multi-select mode: currently selected values */
  selectedValues?: PromptAgentType[];
  /** Multi-select callback */
  onSelectedValuesChange?: (values: PromptAgentType[]) => void;
  /** Selection mode */
  selectMode?: 'single' | 'multi';
  /** Placeholder text when nothing is selected */
  placeholder?: string;
  /** Whether to stretch to fill the parent container width */
  fullWidth?: boolean;
}

export const AgentTypeSelect: React.FC<AgentTypeSelectProps> = ({
  value,
  onChange,
  selectedValues = [],
  onSelectedValuesChange,
  selectMode = 'single',
  placeholder = 'Agent Type',
  fullWidth = false,
}) => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const agentTypes = getAgentTypes();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const isMulti = selectMode === 'multi';
  const isAllSelected = isMulti && selectedValues.length === 0;

  const displayLabel = isMulti
    ? isAllSelected
      ? placeholder
      : selectedValues.length === 1
        ? selectedValues[0]
        : `${selectedValues.length} selected`
    : value || placeholder;

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleToggleAll = () => {
    onSelectedValuesChange?.([]);
  };

  const handleSelectType = (type: PromptAgentType) => {
    if (isMulti) {
      if (selectedValues.includes(type)) {
        onSelectedValuesChange?.(selectedValues.filter((t) => t !== type));
      } else {
        onSelectedValuesChange?.([...selectedValues, type]);
      }
    } else {
      onChange?.(type);
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${fullWidth ? 'block w-full' : 'inline-block'}`}>
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className={`flex items-center justify-between gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${fullWidth ? 'w-full' : ''}`}
        style={{
          ...(!fullWidth && { minWidth: '160px', maxWidth: '220px' }),
          color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#5E95ED',
          border: isDark ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid #5E95ED',
          background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#E8F3FF',
          fontFamily: '3MCircularTT, sans-serif',
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span
          className="truncate top-heavy"
          style={{ textAlign: 'left', lineHeight: '1.3' }}
        >
          {displayLabel}
        </span>
        <ChevronDownIcon
          className="w-4 h-4 transition-transform flex-shrink-0"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>

      {/* Dropdown menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scaleY: 0.8, y: -4 }}
            animate={{ opacity: 1, scaleY: 1, y: 0 }}
            exit={{ opacity: 0, scaleY: 0.8, y: -4 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className={`absolute left-0 mt-1 rounded-lg shadow-lg overflow-hidden origin-top py-1 scrollbar-hide ${fullWidth ? 'w-full' : ''}`}
            style={{
              ...(!fullWidth && { minWidth: '200px' }),
              maxHeight: '280px',
              overflowY: 'auto',
              scrollBehavior: 'smooth',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              background: isDark ? 'rgba(30, 30, 30, 0.95)' : '#FFFFFF',
              border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #5E95ED',
              backdropFilter: 'blur(12px)',
              zIndex: 200,
            }}
            role="listbox"
            aria-multiselectable={isMulti}
          >

            {/* Individual agent types */}
            {agentTypes.map((type) => {
              const isSelected = isMulti
                ? selectedValues.includes(type)
                : value === type;
              return (
                <button
                  key={type}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleSelectType(type)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors"
                  style={{ background: 'transparent', fontFamily: '3MCircularTT, sans-serif' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? 'rgba(255, 255, 255, 0.12)' : '#BFDBFE'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <span
                    className="flex items-center justify-center flex-shrink-0 rounded"
                    style={{
                      width: 16,
                      height: 16,
                      border: isSelected
                        ? 'none'
                        : `1.5px solid ${isDark ? 'rgba(255,255,255,0.3)' : '#9ca3af'}`,
                      background: isSelected
                        ? isDark ? '#82B6FA' : '#2563EB'
                        : 'transparent',
                    }}
                  >
                    {isSelected && <CheckIcon width={12} height={12} style={{ color: '#fff' }} />}
                  </span>
                  <span
                    style={{
                      fontWeight: isSelected ? 600 : 400,
                      marginTop: 2,
                      color: isSelected
                        ? isDark ? '#82B6FA' : '#2563EB'
                        : isDark ? 'rgba(255,255,255,0.85)' : '#374151',
                    }}
                  >
                    {type}
                  </span>
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
